/**
 * components/rightbar/artifactList.ts —— 「产物视图」页（kind=`files`）**数据面**：产物条目 ⇒ 行模型。
 *
 * 真源：`102:frontend_src/src/assembly/models/artifactListModel.js`（逐字译 JS→TS）＋
 *       `102:frontend_src/src/utils/file.js::fileIcon`（新树无对应件，逐字译入本件）。
 *
 * 依据（设计件 §6.4「产物视图」逐字）：
 *   「**本会话产物列表**（`GET /api/files?session_id=&source=&offset=&limit=`），按后端注入的 `group`/`source`
 *     分组（0825 定案）；**行序＝group 序 ＋ 稳定名序**；条目上限截断 ⇒ **标记**；空集**如实说明**；
 *     失败按**错误码**分行。🔴 **不建目录树**」
 *   ／§6.3 地址模型（`sononux://file/session/<sid>/id/<file_id>`）。
 *
 * 🔴 「不建目录树」的判据落点（本件）：产物一律**平铺**——输出只有 `rows[]`（平铺行）＋按 `group` 的**一层**分组
 *    （`groups[]`，每组 `rows` 仍是平铺文件行）；**本件不产出任何父子/层级键**（无 `children`／`parent`／`path`
 *    树字段），文件名里的 `/` **不**参与切分建树。分组定义**单一事实源＝后端** `GET /api/files/group-meta`
 *    （本模型只消费，零硬编码组名——未知 source 落 `FALLBACK_GROUP`）。
 *
 * 归位：**纯函数数据面**（零请求／零 DOM／零响应式）；取数（`/api/files`＋`/files/group-meta`）归宿主，
 *       结果交本件装配 ⇒ `FilesTabBody` 只吃 `model`。
 */
import type { ArtifactGroup, ArtifactListModel, ArtifactRow } from "./FilesTabBody";
import { formatSize } from "./formatSize"; // 🆕 L-AU-HYG 归位：真源＝./formatSize.ts（原从 ./TextTabBody 取）
import { formatSessionTime, normalizeIso } from "./ProgressTabBody";

/** 兜底组 key（后端 `SOURCE_GROUP_MAP` 对未知 source 的兜底组同名） */
export const FALLBACK_GROUP = "对话产物";

/** 资源地址（§6.3 id 基）：`sononux://file/session/<sessionId>/id/<file_id>` */
export function fileAddress(sessionId: string, fileId: string): string {
  return `sononux://file/session/${sessionId}/id/${fileId}`;
}

/**
 * 扩展名 ⇒ 图标名（逐字译自 102 `utils/file.js::fileIcon`；图标名须在 `FilesTabBody::ICON_PATHS` 在册）。
 * 新树 `utils/file.js` 未随包 ⇒ 就地落位（禁各写一份：`FilesTabBody` 与 `TextTabBody` 均从本件取用）。
 */
export function fileIcon(name: string): string {
  const ext = (String(name || "").split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"].includes(ext)) return "image";
  if (["xls", "xlsx", "csv"].includes(ext)) return "sheet";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "archive";
  if (["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) return "audio";
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "video";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["ppt", "pptx"].includes(ext)) return "ppt";
  return "file";
}

/** `GET /api/files` 条目（后端下发；本件只读这几键，多余键不消费） */
export interface ArtifactItem {
  file_id?: string | number;
  id?: string | number;
  filename?: string;
  name?: string;
  source?: string;
  group?: string;
  size?: number | string;
  created_at?: string;
  fileUrl?: string;
}

/** `GET /api/files/group-meta` 的组声明（`{key, sources[], default_expanded}`） */
export interface GroupMeta {
  key?: string;
  sources?: string[];
  default_expanded?: boolean;
}

/** 单行模型（102 `artifactRow` 逐字） */
export function artifactRow(item: ArtifactItem, sessionId: string): ArtifactRow {
  const fileId = String(item.file_id || item.id || "");
  const filename = String(item.filename || item.name || fileId);
  const size = Number(item.size || 0);
  return {
    fileId,
    filename,
    source: String(item.source || ""),
    group: String(item.group || FALLBACK_GROUP),
    size,
    sizeText: formatSize(size),
    icon: fileIcon(filename),
    createdAt: item.created_at || "",
    /** 行外右下角小号时间戳文案（同日 ⇒ HH:mm／昨天／N 天前／M月D日／跨年 ⇒ YYYY-MM-DD；非法 ⇒ ''） */
    createdAtText: formatSessionTime(normalizeIso(item.created_at)),
    fileUrl: item.fileUrl || (fileId ? `/api/files/${fileId}/content` : ""),
    address: sessionId && fileId ? fileAddress(sessionId, fileId) : "",
  };
}

/**
 * 列表模型（分组 ＋ 排序 ＋ 截断标记 ＋ 空集标记）。**平铺，不建目录树**（见文件头判据落点）。
 * @param o.sessionId 会话 id（地址基）
 * @param o.items 本页条目（后端已按 created_at DESC 返回；本模型按「稳定名序」重排）
 * @param o.groups `group-meta` 的 `groups`（`{key, sources[], default_expanded}`）
 * @param o.total 后端 `total`（用于截断标记）
 */
export function buildArtifactList(o: {
  sessionId: string;
  items?: ArtifactItem[] | null;
  groups?: GroupMeta[] | null;
  total?: number | string | null;
}): ArtifactListModel {
  const sessionId = o.sessionId;
  const list = Array.isArray(o.items) ? o.items : [];
  const rows = list.map((it) => artifactRow(it, sessionId));
  const shown = rows.length;
  const rawTotal = o.total;
  const totalCount = rawTotal !== null && rawTotal !== undefined && Number.isFinite(Number(rawTotal))
    ? Number(rawTotal)
    : shown;

  // group 序（取自后端下发；缺省空 ⇒ 按出现序兜底，仍不硬编码组名）
  const order: string[] = [];
  const metaByKey = new Map<string, { key: string; defaultExpanded: boolean; sources: string[] }>();
  for (const g of Array.isArray(o.groups) ? o.groups : []) {
    const key = String(g && g.key ? g.key : "");
    if (!key || metaByKey.has(key)) continue;
    metaByKey.set(key, {
      key,
      defaultExpanded: g.default_expanded !== false,
      sources: Array.isArray(g.sources) ? g.sources.slice() : [],
    });
    order.push(key);
  }

  // 组内「稳定名序」（同名以 file_id 破平；保证同数据同序）——**不按 `/` 切分建树**
  const buckets = new Map<string, ArtifactRow[]>();
  for (const r of rows) {
    const key = r.group ?? FALLBACK_GROUP;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      if (!metaByKey.has(key)) {
        metaByKey.set(key, { key, defaultExpanded: true, sources: [] });
        order.push(key);
      }
    }
    (buckets.get(key) as ArtifactRow[]).push(r);
  }
  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.filename.localeCompare(b.filename) || a.fileId.localeCompare(b.fileId));
  }

  const outGroups: ArtifactGroup[] = order
    .filter((key) => buckets.has(key) && (buckets.get(key) as ArtifactRow[]).length > 0)
    .map((key) => {
      const meta = metaByKey.get(key) as { key: string; defaultExpanded: boolean; sources: string[] };
      const gr = buckets.get(key) as ArtifactRow[];
      return {
        key,
        defaultExpanded: meta.defaultExpanded,
        sources: meta.sources,
        rows: gr,
        count: gr.length,
      };
    });

  return {
    groups: outGroups,
    rows,
    total: totalCount,
    shown,
    truncated: totalCount > shown,
    empty: shown === 0,
  };
}
